import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

/**
 * Sync state with URL search params.
 * Used for filter state on Activity, Orders, Catalog pages.
 *
 * @param key - URL param key
 * @param defaultValue - Default value if param is missing
 * @returns [value, setValue] tuple
 *
 * @example
 * const [search, setSearch] = useUrlState('search', '');
 * const [page, setPage] = useUrlState('page', 0);
 */
export function useUrlState<T extends string | number | boolean>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo(() => {
    const raw = searchParams.get(key);
    if (raw === null) return defaultValue;

    // Type-safe deserialization
    if (typeof defaultValue === 'number') {
      const num = Number(raw);
      return (Number.isNaN(num) ? defaultValue : num) as T;
    }
    if (typeof defaultValue === 'boolean') {
      return (raw === 'true') as T;
    }
    return raw as T;
  }, [searchParams, key, defaultValue]);

  const setValue = useCallback(
    (newValue: T) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (newValue === defaultValue || newValue === '' || newValue === null) {
          next.delete(key);
        } else {
          next.set(key, String(newValue));
        }
        return next;
      });
    },
    [key, defaultValue, setSearchParams],
  );

  return [value, setValue];
}
